import { Check, SlidersHorizontal } from 'lucide-react';
import { PlatformIcon } from '@/components/ui/platform-icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ChatFeedStore, PlatformFilter } from '@/features/chat-feed/store';

const PLATFORMS: { value: PlatformFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'youtube', label: 'YouTube' },
];

function RadioRow({
  selected,
  label,
  icon,
  onClick,
}: {
  selected: boolean;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={
        'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors duration-fast ' +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
        (selected ? 'bg-primary/15 text-fg' : 'text-fg-muted hover:bg-bg-muted hover:text-fg')
      }
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <span className="flex-1">{label}</span>
      {selected && <Check size={14} strokeWidth={2} className="text-primary" aria-hidden="true" />}
    </button>
  );
}

function CheckboxRow({
  checked,
  label,
  desc,
  onChange,
}: {
  checked: boolean;
  label: string;
  desc?: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-start gap-3 w-full px-2 py-1.5 rounded-md text-left transition-colors duration-fast hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span
        aria-hidden="true"
        className={
          'mt-0.5 inline-flex w-4 h-4 items-center justify-center rounded border transition-colors duration-fast ' +
          (checked ? 'bg-primary border-primary text-primary-fg' : 'border-border bg-bg')
        }
      >
        {checked && <Check size={11} strokeWidth={3} />}
      </span>
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm text-fg">{label}</span>
        {desc && <span className="text-xs text-fg-muted">{desc}</span>}
      </span>
    </button>
  );
}

interface Props {
  store: ChatFeedStore;
}

export function FiltersPopover({ store }: Props) {
  const filter = store((s) => s.filter);
  const setFilter = store((s) => s.setFilter);
  const active =
    filter.platform !== 'all' || filter.onlyMods;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={active ? 'Filtros (ativos)' : 'Filtros'}
          title="Filtros"
          className={
            'relative w-8 h-8 grid place-items-center rounded-md transition-colors duration-fast ' +
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
            (active
              ? 'text-primary bg-primary/15 hover:bg-primary/20'
              : 'text-fg-muted hover:bg-bg-muted hover:text-fg')
          }
        >
          <SlidersHorizontal size={16} strokeWidth={1.75} />
          {active && (
            <span
              aria-hidden="true"
              className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <div className="flex flex-col gap-3">
          <div>
            <div
              className="text-[11px] font-semibold uppercase tracking-wide text-fg-subtle px-2 pb-1.5"
              role="group"
              aria-label="Plataforma"
            >
              Plataforma
            </div>
            <div role="radiogroup" aria-label="Plataforma" className="flex flex-col gap-0.5">
              {PLATFORMS.map(({ value, label }) => (
                <RadioRow
                  key={value}
                  selected={filter.platform === value}
                  label={label}
                  icon={
                    value === 'all' ? null : (
                      <PlatformIcon platform={value} size={12} />
                    )
                  }
                  onClick={() => setFilter({ platform: value })}
                />
              ))}
            </div>
          </div>

          <div className="h-px bg-border" aria-hidden="true" />

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-subtle px-2 pb-1.5">
              Autor
            </div>
            <CheckboxRow
              checked={filter.onlyMods}
              label="Apenas moderadores"
              desc="Mostra somente msgs de mods e broadcaster."
              onChange={(next) => setFilter({ onlyMods: next })}
            />
          </div>

          {active && (
            <button
              type="button"
              onClick={() => setFilter({ platform: 'all', onlyMods: false })}
              className="text-xs text-fg-muted hover:text-fg px-2 py-1.5 rounded-md hover:bg-bg-muted transition-colors duration-fast self-start"
            >
              Limpar filtros
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
